import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	CircularProgress,
	GlobalStyles,
	Paper,
	Typography,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	ExcelRow,
	GridApi,
	GridReadyEvent,
	RowClassParams,
} from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import moment, { type Moment } from "moment";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";

import { ProductTypesStatisticsToolbar } from "./ProductTypesStatisticsToolbar";
import { useProductTypesStatisticsData } from "../hooks/useProductTypesStatistics";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import {
	isTotalRow,
	type ProductTypesStatisticsRow,
} from "../models/ProductTypesStatistics";

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";
const HEADER_HEIGHT = 32;
const ROW_HEIGHT = 28;
const NO_ROWS_OVERLAY = `<span style="color:${mesfColors.text.secondary};font-size:13px">No campaigns were rolled in the selected range</span>`;
const TOTAL_ROW = {
	backgroundColor: mesfColors.grey[100],
	fontWeight: 600,
	borderTop: `2px solid ${mesfColors.grey[300]}`,
} as const;

// Every column renders its raw value: the legacy screen prints all seven through a plain
// string cell, so PreparationHours arrives as 0.141000 and TPH as 101.23148148148148.
//
// Widths are flex, not fixed. autoSizeStrategy runs once, when the grid mounts, and an
// action that reuses the cached result - Last 12 Months over the range the screen already
// holds - re-renders the rows without remounting, which drops every column back to its
// declared width and leaves half the grid blank. flex is re-applied on every layout pass.
const COLUMN_DEFS: ColDef<ProductTypesStatisticsRow>[] = [
	{
		field: "ProductSize",
		headerName: "Product Size",
		flex: 20,
		minWidth: 180,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "PreparationHours",
		headerName: "Preparation Hours",
		flex: 15,
		minWidth: 150,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Billets",
		headerName: "Billets",
		flex: 10,
		minWidth: 100,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Tons",
		headerName: "Tons",
		flex: 14,
		minWidth: 140,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "MillTimeHours",
		headerName: "Mill Time Hours",
		flex: 14,
		minWidth: 140,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "DelayTimeHours",
		headerName: "Delay Time Hours",
		flex: 15,
		minWidth: 145,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "TPH",
		headerName: "TPH",
		flex: 14,
		minWidth: 140,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
];

const EXPORT_FIELDS = COLUMN_DEFS.map(
	(col) => col.field,
) as (keyof ProductTypesStatisticsRow)[];

// The window the legacy grid hardcodes. Both the initial state and Reset read it from
// here, so they cannot drift apart.
const defaultStart = () => moment().startOf("day").subtract(365, "days");
const defaultEnd = () => moment().startOf("day");

const URL_SCHEMA = {
	start: urlParam.date(defaultStart),
	end: urlParam.date(defaultEnd),
};

export const ProductTypesStatisticsReport = () => {
	const [{ start, end }, setFilters, { resetValues }] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<ProductTypesStatisticsRow> | null>(null);

	// The procedure compares campaign timestamps, not dates, so an end of 00:00 would drop
	// everything rolled on the day the user picked.
	const startStr = start.format("YYYY-MM-DD 00:00:00");
	const endStr = end.format("YYYY-MM-DD 23:59:59");

	const dataQuery = useProductTypesStatisticsData({
		start: startStr,
		end: endStr,
	});

	// The rollup row travels inside the same result set. Pinning it keeps it under the last
	// product and out of the sort, which the legacy grid loses the moment a header is clicked.
	const { rows, totalRows } = useMemo(() => {
		const all = dataQuery.data ?? [];
		const productRows = all.filter((row) => !isTotalRow(row));
		return {
			rows: productRows,
			totalRows: productRows.length ? all.filter(isTotalRow) : [],
		};
	}, [dataQuery.data]);

	const handleGridReady = useCallback(
		(event: GridReadyEvent<ProductTypesStatisticsRow>) => {
			gridApiRef.current = event.api;
		},
		[],
	);

	const handleStartChange = useCallback(
		(d: Moment | null) => {
			if (isUsableDate(d)) setFilters({ start: d });
		},
		[setFilters],
	);

	const handleEndChange = useCallback(
		(d: Moment | null) => {
			if (isUsableDate(d)) setFilters({ end: d });
		},
		[setFilters],
	);

	const handleLastTwelveMonths = useCallback(() => {
		setFilters({ start: end.clone().subtract(365, "days") });
	}, [end, setFilters]);

	// Back to the window the screen opens on. When the dates are already the default they
	// do not change, so nothing would refetch — the explicit refetch is what makes the
	// button also pick up campaigns closed since the page loaded.
	const handleReset = useCallback(() => {
		resetValues();
		dataQuery.refetch();
	}, [dataQuery, resetValues]);

	// The legacy workbook carries the Total row, so it is appended by hand — AG Grid leaves
	// pinned rows out of the export.
	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		const appendContent: ExcelRow[] = totalRows.map((row) => ({
			cells: EXPORT_FIELDS.map((field) => {
				const value = row[field];
				return typeof value === "number"
					? { data: { value: String(value), type: "Number" as const } }
					: { data: { value: value ?? "", type: "String" as const } };
			}),
		}));

		gridApiRef.current.exportDataAsExcel({
			fileName: "Product_Types_Statistics.xlsx",
			appendContent,
		});
	}, [totalRows]);

	const gridBoxRef = useRef<HTMLDivElement | null>(null);
	const [gridSpace, setGridSpace] = useState(0);

	useLayoutEffect(() => {
		const el = gridBoxRef.current;
		if (!el) return;
		const update = () => setGridSpace(el.clientHeight);
		update();
		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const contentHeight =
		HEADER_HEIGHT + (rows.length + totalRows.length) * ROW_HEIGHT;
	const fitsWithoutScroll = gridSpace > 0 && contentHeight <= gridSpace;

	const getRowStyle = useCallback(
		(params: RowClassParams<ProductTypesStatisticsRow>) =>
			params.node.rowPinned === "bottom" ? TOTAL_ROW : undefined,
		[],
	);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Product Types Statistics
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
					// In autoHeight AG Grid keeps a minimum height on the rows viewport for the
					// no-rows overlay, which becomes a gap above the pinned Total row.
					".ag-layout-auto-height .ag-center-cols-clipper, .ag-layout-auto-height .ag-center-cols-viewport":
						{
							minHeight: 0,
						},
				}}
			/>
			<Paper elevation={3}>
				<ProductTypesStatisticsToolbar
					start={start}
					end={end}
					onStartChange={handleStartChange}
					onEndChange={handleEndChange}
					onLastTwelveMonths={handleLastTwelveMonths}
					onReset={handleReset}
					onExportExcel={handleExportToExcel}
					isLoading={dataQuery.isFetching}
				/>
			</Paper>

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden" }}>
				<Box ref={gridBoxRef} sx={{ height: "100%" }}>
					{dataQuery.isLoading ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
							}}
						>
							<CircularProgress />
						</Box>
					) : (
						<AgGridReact<ProductTypesStatisticsRow>
							columnDefs={COLUMN_DEFS}
							rowData={rows}
							pinnedBottomRowData={totalRows}
							getRowStyle={getRowStyle}
							rowModelType="clientSide"
							domLayout={
								rows.length > 0 && fitsWithoutScroll ? "autoHeight" : "normal"
							}
							pagination={false}
							defaultColDef={{ resizable: true, sortable: true }}
							suppressContextMenu
							overlayNoRowsTemplate={NO_ROWS_OVERLAY}
							headerHeight={HEADER_HEIGHT}
							rowHeight={ROW_HEIGHT}
							onGridReady={handleGridReady}
						/>
					)}
				</Box>
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
